import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';

import { Dialog } from '@material/mwc-dialog';
import { MAP_STYLES_COLLECTION } from '../../../helpers/constants';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import { TextArea } from '@material/mwc-textarea';
import topicData from '../../../helpers/state/topic-data';
import { undefinedIfEmpty } from '../../../helpers/utils';
import { getMainPage } from '../../../helpers/page-name';

@customElement('map-skin')
export class MapSkin extends LitElement {

    @query('#customMapStyleModal')
    customMapStyleModal!: Dialog;

    @query('#customMapStyle')
    private readonly customMapStyleElement?: TextArea;

    @state()
    selectedMapStyle: string = 'standard';

    @state()
    selectedMapGeoJson: string = '';

    initialMapStyleName: string = '';

    private readonly bindTopicState = new StateController(this, topicData);
    errorToastElement: any;

    firstUpdated() {
        this.selectedMapStyle = topicData.topic?.custom_map_style_name ?? 'standard';
        this.initialMapStyleName = topicData.topic?.custom_map_style_name ?? 'standard';
    }

    async toggleHideHeader(e: Event) {
        const hideHeader = (e.target as HTMLInputElement).checked;

       await topicData.fetchAndUpdateTopicData({
            hide_header: hideHeader
        }, 'hide_header');
    }

    private async selectMapStyle(title: string) {
        const previous_is_custom = topicData.topic?.custom_map_style_name === 'custom';
        if (title === 'custom') {
            this.customMapStyleModal.open = true;
        } else {
            await topicData.fetchAndUpdateTopicData({
                custom_map_style_name: title,
                custom_map_style: this.selectedMapGeoJson
            }, 'map-skin', true, true);
        }

        if (previous_is_custom && title !== 'custom') {
            location.reload();
        }
        this.selectedMapStyle = title;
    }

    private async saveCustomStyle() {
        this.selectedMapStyle = 'custom';

        const custom_map_style = undefinedIfEmpty(
            this.customMapStyleElement?.value.trim()
        );

        /// check if custom map style is valid
        try {
            JSON.parse(custom_map_style ?? '');
        } catch (e) {
            getMainPage().errorToastElement.showToastAndThrow('The code submitted looks invalid');
        }

        await topicData.fetchAndUpdateTopicData({
            custom_map_style_name: 'custom',
            custom_map_style
        }, 'map-skin', true, true);

        this.customMapStyleModal.close();
        location.reload();
    }

    render() {
        return html`
            <h4 class="helpSubHeader">Map Design</h4>
            <br>
            <div class="mapStyleCollection">
                ${MAP_STYLES_COLLECTION.filter((item) => item.private !== true || item.organization_id === topicData.topic?.organization_id?.$oid)
                    .map((item) => html`
                <div
                    class="mapStyleItem ${this.selectedMapStyle === item.title ? 'active' : ''}"
                    @click=${async () => await this.selectMapStyle(item.title)}>
                    <img src=${item.img} />
                    ${item.label}
                    ${item.title === 'standard' ? html`<span class="a11y-badge">Best for accessibility</span>` : ''}
                </div>
                `)}
            </div>
            <mwc-dialog id="customMapStyleModal" class="dialog" hideActions>
                <modal-dialog size="md" @close="${() => this.customMapStyleModal.close()}">
                    <div slot="body">
                        <h3 class="modalHeader">Add Custom Map Style</h3>
                        <p>
                            You can customize the map coloring, detail, and more by finding a style you like
                            or making your own on
                            <a href="https://mapstyle.withgoogle.com/" target="_blank">Google Map Styler</a>
                            or
                            <a href="https://snazzymaps.com/explore" target="_blank">SnazzyMaps</a>. Copying
                            the custom code JSON into this text box.
                            <br>
                            Note: using a custom map style may affect the performance of the map. If you would like us to add your custom style to the default map styles, please contact us.
                        </p>
                        <mwc-textarea
                            id="customMapStyle"
                            class="input"
                            label="Custom Map Style"
                            value="${topicData.topic?.custom_map_style ?? ''}"
                            charcounter
                            rows="5"
                            maxlength="10000">
                        </mwc-textarea>
                        <div class="icon__actions">
                            <form-button variant="social" @click="${() => this.customMapStyleModal.close()}">
                                Cancel
                            </form-button>
                            <form-button @click="${this.saveCustomStyle}">
                                Save
                            </form-button>
                        </div>
                    </div>
                </modal-dialog>
            </mwc-dialog>


        `;
    }

    static get styles() {
        return css`

            .header-text-label {
                margin: 10px 0;
            }

            h4 {
                display: flex;
                align-items: center;
                margin-bottom: 0;
                margin-top: 0;
            }

            .gatedField {
                display: flex;
                flex-direction: column;
                cursor: pointer;
            }

            .menuPng {
                margin-left: 0.25rem;
                height: 20px;
            }

            .style__wrapper {
                height:100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            .mapStyleCollection {
                display: grid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
                grid-gap: 0.5rem;

            }

            .style__section {
                padding: 1rem 2rem;
                flex: 1;
                overflow-y: auto;
                overflow-x: hidden;
            }

            .map-style-component__tabs {
                display: flex;
                justify-content: flex-start;
            }

            .map-style-component__tab {
                padding-bottom: 0.25rem;
                line-height: 1.5;
                color: var(--theme-lighter-text-color);
                cursor: pointer;
                margin-right: 8px;
                flex: 1;
                text-align: center;
            }

            .map-style-component__tab.active {
                font-weight: 700;
                border-bottom: 2px solid var(--main-secondary-color);
                color: var(--main-secondary-color);
            }

            .mapStyleItem {
                text-align: center;
                cursor: pointer;
            }

            .mapStyleItem.active {
                font-weight: 700;
            }

            .mapStyleItem img {
                width: 100%;
                object-fit: cover;
                height: 3.5rem;
                border-radius: 0.75rem;
                border: 2px solid transparent;
                box-shadow: 0px 2px 8px -4px rgba(31, 27, 45, 0.12);
            }

            .header-with-info {
                display: flex;
                flex-direction: row;
                align-items: center;
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .mapStyleItem.active img {
                border: 2px solid var(--main-primary-color);
            }

            .a11y-badge {
                display: block;
                font-size: 0.55rem;
                color: #166534;
                background: #dcfce7;
                border-radius: 4px;
                padding: 1px 4px;
                margin-top: 2px;
                white-space: nowrap;
            }

            .icon__actions {
                padding-top: 18px;
                display: flex;
                justify-content: space-evenly;
            }
		    .input {
                width: 100%;
                padding: auto 5px;
            }

            .style__actions {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                padding: 1rem 0.5rem;
                gap: 0.5rem;
            }
        `;
    }
}
